import axios, { AxiosError } from 'axios';
import {
  CreateSessionRequest,
  CreateSessionResponse,
  SwipeRequest,
  SwipeResponse,
  Session,
  Restaurant,
  Match,
  City,
  CatalogResponse,
} from '../types';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001',
  timeout: 15000,
});

// Человекочитаемые ошибки на русском
const ERROR_MESSAGES: Record<number, string> = {
  400: 'Некорректный запрос',
  404: 'Не найдено',
  408: 'Время ожидания истекло',
  429: 'Слишком много запросов, подождите',
  500: 'Ошибка сервера',
  502: 'Сервер недоступен',
  503: 'Сервер временно недоступен',
};

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string }>) => {
    let message: string;

    if (!error.response) {
      message = 'Нет подключения к серверу';
    } else {
      // Предпочитаем сообщение от бэкенда, иначе — стандартное
      message =
        error.response.data?.error ||
        ERROR_MESSAGES[error.response.status] ||
        'Произошла ошибка';
    }

    // Сохраняем человекочитаемое сообщение в error
    const enrichedError = new Error(message) as Error & { status?: number };
    enrichedError.status = error.response?.status;
    return Promise.reject(enrichedError);
  }
);

export const sessionApi = {
  create: async (data: CreateSessionRequest): Promise<CreateSessionResponse> => {
    const res = await api.post('/api/sessions', data);
    return res.data;
  },

  get: async (sessionId: string): Promise<{ session: Session }> => {
    const res = await api.get(`/api/sessions/${sessionId}`);
    return res.data;
  },

  join: async (sessionId: string, nickname: string): Promise<{ participantId: string }> => {
    const res = await api.post(`/api/sessions/${sessionId}/join`, { nickname });
    return res.data;
  },

  getRestaurants: async (
    sessionId: string,
    participantId: string
  ): Promise<{ restaurants: Restaurant[]; remainingCount: number; totalCount: number }> => {
    const res = await api.get(`/api/sessions/${sessionId}/restaurants`, {
      params: { participantId },
    });
    return res.data;
  },

  getMatches: async (sessionId: string): Promise<{ matches: Match[] }> => {
    const res = await api.get(`/api/sessions/${sessionId}/matches`);
    return res.data;
  },
};

export const swipeApi = {
  swipe: async (sessionId: string, data: SwipeRequest): Promise<SwipeResponse> => {
    const res = await api.post(`/api/sessions/${sessionId}/swipe`, data);
    return res.data;
  },
};

export interface RubricInfo {
  id: string;
  name: string;
  restaurantCount: number;
}

export const restaurantApi = {
  getCities: async (): Promise<{ cities: City[] }> => {
    const res = await api.get('/api/restaurants/cities');
    return res.data;
  },

  getRubrics: async (citySlug?: string): Promise<{ rubrics: RubricInfo[] }> => {
    const res = await api.get('/api/restaurants/rubrics', { params: { citySlug } });
    return res.data;
  },

  getCatalog: async (params: {
    citySlug?: string;
    search?: string;
    rubrics?: string[];
    cuisineTags?: string[];
    menuTags?: string[];
    features?: string[];
    minRating?: number;
    sortBy?: string;
    groupChains?: boolean;
    page?: number;
    limit?: number;
  }): Promise<CatalogResponse> => {
    const res = await api.get('/api/restaurants/catalog', {
      params: {
        citySlug: params.citySlug,
        search: params.search || undefined,
        rubrics: params.rubrics?.join(',') || undefined,
        cuisineTags: params.cuisineTags?.join(',') || undefined,
        menuTags: params.menuTags?.join(',') || undefined,
        features: params.features?.join(',') || undefined,
        minRating: params.minRating || undefined,
        sortBy: params.sortBy || undefined,
        groupChains: params.groupChains ? 'true' : undefined,
        page: params.page || 1,
        limit: params.limit || 50,
      },
    });
    return res.data;
  },

  getBranches: async (chainName: string, citySlug = 'tomsk'): Promise<{ branches: Branch[] }> => {
    const res = await api.get('/api/restaurants/branches', {
      params: { chainName, citySlug },
    });
    return res.data;
  },
};

export interface Branch {
  id: string;
  externalId: string;
  name: string;
  address: string;
  rating?: number;
  reviewCount?: number;
  averageCheck?: string;
  photoUrl?: string;
  phone?: string;
  website?: string;
  latitude: number;
  longitude: number;
  workingHours?: Record<string, unknown>;
}

export interface UserPreferences {
  likedCuisines: string[];
  dislikedCuisines: string[];
  likedMenuTags: string[];
  dislikedMenuTags: string[];
  isConfigured: boolean;
}

export const preferencesApi = {
  get: async (): Promise<UserPreferences> => {
    const res = await api.get('/api/preferences');
    return res.data;
  },

  update: async (data: Omit<UserPreferences, 'isConfigured'>): Promise<UserPreferences> => {
    const res = await api.put('/api/preferences', data);
    return res.data;
  },
};

export const favoriteApi = {
  list: async (): Promise<{ restaurantIds: string[] }> => {
    const res = await api.get('/api/favorites');
    return res.data;
  },

  add: async (restaurantId: string): Promise<void> => {
    await api.post(`/api/favorites/${restaurantId}`);
  },

  remove: async (restaurantId: string): Promise<void> => {
    await api.delete(`/api/favorites/${restaurantId}`);
  },
};

export default api;
